'use client'
import { Flame } from 'lucide-react'
import type { TierKey } from '@/components/phishtrainer/me/medallions/tier-palette'
import { TierMedallion, type MedallionState } from '@/components/phishtrainer/me/medallions/tier-medallion'
import { RayBurstBackdrop } from '@/components/phishtrainer/me/medallions/ray-burst-backdrop'
import { StarIcon } from '@/components/phishtrainer/me/medallions/star-icon'
import { WeekStatsStrip } from './week-stats-strip'

export interface TierHeroBandProps {
  userName: string
  currentStars: number
  currentTier: TierKey
  peakTier: TierKey
  nextTier: TierKey | null
  starsToNextTier: number
  progressToNextTier: number
  streakCurrent: number
  weekStats: { rounds: number; passed: number; perfect: number; starsEarned: number }
  tiers: Array<{ key: TierKey; name: string; threshold: number }>
  /** Optional href for the "How the game works" link below the progress bar. */
  howItWorksHref?: string
}

export function TierHeroBand(props: TierHeroBandProps) {
  const { userName, currentStars, currentTier, peakTier, nextTier, starsToNextTier, progressToNextTier, streakCurrent, weekStats, tiers } = props
  const currentIdx = tiers.findIndex((t) => t.key === currentTier)
  const peakIdx = tiers.findIndex((t) => t.key === peakTier)
  const nextDef = nextTier ? tiers.find((t) => t.key === nextTier) : null

  return (
    <div data-hero-band className="relative overflow-hidden rounded-2xl border border-border bg-card">
      <RayBurstBackdrop activeIndex={currentIdx} totalTiers={tiers.length} />
      <div className="relative px-3 pt-5 pb-4 sm:px-6 sm:pt-7 sm:pb-5">
        {/* Greeting row */}
        <div className="mb-4 sm:mb-5 flex items-end justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-primary">Welcome back</div>
            <div className="text-lg sm:text-2xl font-bold text-foreground truncate">{userName}</div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 rounded-full bg-muted border border-border px-2 sm:px-3 py-1 sm:py-1.5 text-[11px] sm:text-xs text-muted-foreground shrink-0">
            <Flame className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-orange-500" />
            <span className="font-semibold text-foreground tabular-nums">{streakCurrent}</span>
            <span className="hidden sm:inline">-day streak</span>
            <span className="sm:hidden">day</span>
          </div>
        </div>

        {/* 5 medallions row */}
        <div className="flex items-end justify-between gap-1 sm:gap-2 max-w-5xl mx-auto">
          {tiers.map((t, i) => {
            const state: MedallionState = i < currentIdx ? 'past' : i === currentIdx ? 'active' : 'locked'
            const sizeClass =
              state === 'active' ? 'w-[72px] sm:w-28 md:w-36'
                : state === 'past' ? 'w-[44px] sm:w-20 md:w-[86px]'
                : 'w-[40px] sm:w-[72px] md:w-[82px]'
            return (
              <div key={t.key} className="flex flex-col items-center flex-1 min-w-0">
                <TierMedallion tier={t.key} state={state} className={sizeClass} />
                <div className="mt-1.5 sm:mt-2 text-center w-full px-0.5">
                  <div className={'text-[10px] sm:text-sm font-semibold tracking-wide truncate ' +
                    (state === 'active' ? 'text-foreground' : state === 'past' ? 'text-muted-foreground' : 'text-muted-foreground/60')
                  }>{t.name}</div>
                  {state === 'active' && (
                    <div className="mt-0.5 sm:mt-1 inline-flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2.5 py-0.5 rounded-full bg-muted border border-border text-[10px] sm:text-xs text-foreground">
                      <StarIcon size={10} />
                      <span className="font-semibold tabular-nums">{currentStars}</span>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Gradient progress bar */}
        <div className="max-w-5xl mx-auto mt-5 sm:mt-6 px-1 sm:px-6">
          <div className="relative h-2 sm:h-2.5 rounded-full bg-muted overflow-hidden">
            <div className="absolute inset-y-0 left-0 rounded-full"
              style={{
                width: `${progressToNextTier * 100}%`,
                background: 'linear-gradient(90deg, #ec4899 0%, #a855f7 45%, #f59e0b 80%, #facc15 100%)',
              }}
            />
            {tiers.map((t, i) => (
              <div key={t.key} className="absolute top-1/2 -translate-y-1/2 w-0.5 h-3 bg-border"
                style={{ left: `${(i / (tiers.length - 1)) * 100}%` }} />
            ))}
          </div>
          <div className="mt-1.5 sm:mt-2 flex items-center justify-between text-[9px] sm:text-[10px] text-muted-foreground tabular-nums">
            {tiers.map((t) => <span key={t.key}>{t.threshold}</span>)}
          </div>
          {nextDef ? (
            <p className="mt-2.5 sm:mt-3 text-center text-xs sm:text-sm text-muted-foreground">
              You are{' '}
              <span className="font-semibold text-foreground inline-flex items-center gap-1">
                <StarIcon size={12} /> {starsToNextTier}
              </span>{' '}
              stars from <span className="font-semibold text-foreground">{nextDef.name}</span>
            </p>
          ) : (
            <p className="mt-2.5 sm:mt-3 text-center text-xs sm:text-sm text-muted-foreground">
              You&apos;ve hit the top tier. <span className="text-foreground font-semibold">Hold it.</span>
            </p>
          )}
          {peakIdx > currentIdx && (
            <p className="mt-1 text-center text-[10px] sm:text-xs text-amber-600 dark:text-amber-400">
              Peak: {tiers[peakIdx].name} — reclaim your tier.
            </p>
          )}
          {props.howItWorksHref && (
            <div className="mt-2 text-center">
              <a href={props.howItWorksHref} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                New here? See how the game works →
              </a>
            </div>
          )}
        </div>

        {/* This-week stats strip */}
        <div className="mt-5 sm:mt-6 max-w-5xl mx-auto">
          <WeekStatsStrip stats={weekStats} />
        </div>
      </div>
    </div>
  )
}
